import React, { useState, useEffect, useRef } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { User } from 'lucide-react';
import { photoUploadService } from '@/services/photoUploadService';

interface SecureProfilePhotoProps {
  filePath?: string | null;
  fallbackInitials?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const SecureProfilePhoto: React.FC<SecureProfilePhotoProps> = ({
  filePath,
  fallbackInitials = 'U',
  size = 'md',
  className = ''
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const currentFilePathRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-24 h-24',
    lg: 'w-32 h-32'
  };

  useEffect(() => {
    isMountedRef.current = true;
    
    const loadImage = async () => {
      // If no file path, clear everything
      if (!filePath) {
        setImageUrl(null);
        setError(false);
        setIsLoading(false);
        currentFilePathRef.current = null;
        return;
      }

      // Don't reload if it's the same file path and we already have it loaded successfully
      if (currentFilePathRef.current === filePath && imageUrl && !error) {
        return;
      }

      // Don't reload if we're already loading this file path
      if (currentFilePathRef.current === filePath && isLoading) {
        return;
      }

      currentFilePathRef.current = filePath;
      setError(false);
      setIsLoading(true);

      try {
        // Check if the filePath is already a full URL (starts with http)
        if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
          if (isMountedRef.current && currentFilePathRef.current === filePath) {
            setImageUrl(filePath);
            setIsLoading(false);
          }
        } 
        // Check if it's a Supabase storage URL
        else if (filePath.includes('supabase.co/storage')) {
          if (isMountedRef.current && currentFilePathRef.current === filePath) {
            setImageUrl(filePath);
            setIsLoading(false);
          }
        }
        // Otherwise, treat as storage path and generate signed URL
        else {
          const signedUrl = await photoUploadService.getProfilePhotoUrl(filePath);
          
          if (isMountedRef.current && currentFilePathRef.current === filePath) {
            if (signedUrl) {
              setImageUrl(signedUrl);
            } else {
              setError(true);
            }
            setIsLoading(false);
          }
        }
      } catch (err) {
        if (isMountedRef.current && currentFilePathRef.current === filePath) {
          setError(true);
          setIsLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      isMountedRef.current = false;
    };
  }, [filePath]); // Only depend on filePath to prevent infinite loops

  const handleImageLoad = () => {
    setError(false);
  };

  const handleImageError = () => {
    setError(true);
    setImageUrl(null);
  };


  return (
    <Avatar className={`${sizeClasses[size]} ${className}`}>
      {imageUrl && !error && !isLoading && (
        <AvatarImage 
          src={imageUrl} 
          alt="Profile photo"
          className="object-cover"
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      )}
      <AvatarFallback className="bg-gray-700 text-white">
        {fallbackInitials}
      </AvatarFallback>
    </Avatar>
  );
};
